import { useCallback, useMemo, useState } from "react";
import { Check, Loader2, Search, SlidersHorizontal, X } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  byInitial, filterGroups, groupCategories, isGroupSelected, toggleGroup, type CategoryGroup,
} from "@/lib/category-groups";
import type { CatalogCategory } from "@/services/wholesale-catalog";

export const ALL_STORES = "__all__";

export type ShowroomFilterState = {
  store: string;
  cats: string[];
  inStock: boolean;
};

type Source = { id: string; name: string };

const focusRing =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-1 focus-visible:ring-offset-background";

/** Broj aktivnih filtera za značku na dugmetu. „Samo na zalihi" je podrazumijevano stanje,
 *  pa se broji tek kad je isključeno — značka govori koliko je katalog drukčiji od početnog. */
export function activeFilterCount(f: ShowroomFilterState, groups: CategoryGroup[]): number {
  const catGroups = groups.filter((g) => isGroupSelected(g, f.cats)).length;
  return (f.store !== ALL_STORES ? 1 : 0) + catGroups + (f.inStock ? 0 : 1);
}

export function FilterButton({ count, onClick }: { count: number; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex h-10 shrink-0 items-center gap-2 border px-3 text-xs transition-colors",
        count > 0
          ? "border-foreground bg-foreground text-background"
          : "border-input text-foreground hover:border-foreground/40",
        focusRing,
      )}
    >
      <SlidersHorizontal className="h-4 w-4" />
      <span className="hidden sm:inline">Filteri</span>
      {count > 0 && <span className="tabular-nums">{count}</span>}
    </button>
  );
}

/** Ono što trenutno sužava katalog, ispisano ispod trake — svaki token se skida jednim
 *  dodirom, bez otvaranja sheeta. Kupac koji gleda u ekran vidi zašto lista izgleda tako. */
export function ActiveFilters({
  state, onChange, sources, groups, onReset,
}: {
  state: ShowroomFilterState;
  onChange: (next: ShowroomFilterState) => void;
  sources: Source[];
  groups: CategoryGroup[];
  onReset: () => void;
}) {
  const storeName = sources.find((s) => s.id === state.store)?.name;
  const selected = groups.filter((g) => isGroupSelected(g, state.cats));
  const tokens: { key: string; label: string; remove: () => void }[] = [];
  if (storeName) tokens.push({ key: "store", label: storeName, remove: () => onChange({ ...state, store: ALL_STORES }) });
  for (const g of selected) {
    tokens.push({ key: g.key, label: g.name, remove: () => onChange({ ...state, cats: toggleGroup(g, state.cats) }) });
  }
  if (!state.inStock) tokens.push({ key: "stock", label: "I rasprodano", remove: () => onChange({ ...state, inStock: true }) });
  if (tokens.length === 0) return null;

  return (
    <div className="-mx-3 flex items-center gap-1.5 overflow-x-auto px-3 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0">
      {tokens.map((t) => (
        <button
          key={t.key}
          type="button"
          onClick={t.remove}
          aria-label={`Ukloni filter ${t.label}`}
          className={cn(
            "group inline-flex h-7 shrink-0 items-center gap-1.5 whitespace-nowrap border border-foreground/25 bg-muted/40 pl-2.5 pr-1.5 text-xs transition-colors hover:border-foreground",
            focusRing,
          )}
        >
          {t.label}
          <X className="h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground" />
        </button>
      ))}
      <button
        type="button"
        onClick={onReset}
        className={cn("h-7 shrink-0 px-2 text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline", focusRing)}
      >
        Poništi
      </button>
    </div>
  );
}

function Segment({
  options, value, onChange, label,
}: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
  label: string;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(o.value)}
            className={cn(
              "h-9 border px-3 text-xs transition-colors",
              active
                ? "border-foreground bg-foreground font-medium text-background"
                : "border-input text-muted-foreground hover:border-foreground/40 hover:text-foreground",
              focusRing,
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function CategoryRow({ group, checked, onToggle }: { group: CategoryGroup; checked: boolean; onToggle: () => void }) {
  return (
    <li>
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        onClick={onToggle}
        className={cn(
          "flex min-h-10 w-full items-center gap-3 px-5 py-2 text-left text-sm transition-colors hover:bg-muted/60",
          focusRing,
          "focus-visible:ring-inset focus-visible:ring-offset-0",
        )}
      >
        <span
          aria-hidden
          className={cn(
            "flex h-4 w-4 shrink-0 items-center justify-center border",
            checked ? "border-foreground bg-foreground text-background" : "border-foreground/30",
          )}
        >
          {checked && <Check className="h-3 w-3" strokeWidth={3} />}
        </span>
        <span className={cn("min-w-0 flex-1", checked && "font-medium")}>{group.name}</span>
        <span className="tabular-nums text-xs text-muted-foreground">{group.count.toLocaleString("bs-BA")}</span>
      </button>
    </li>
  );
}

/** Sheet sa svim filterima prezentacije. Izbor se primjenjuje odmah — mreža iza sheeta se
 *  već mijenja — a dugme u dnu samo zatvara i kaže koliko je artikala ostalo. */
export function ShowroomFilterSheet({
  open, onOpenChange, state, onChange, onReset, sources, categories, total, counting,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  state: ShowroomFilterState;
  onChange: (next: ShowroomFilterState) => void;
  onReset: () => void;
  sources: Source[];
  categories: CatalogCategory[];
  total: number;
  counting: boolean;
}) {
  const [catQuery, setCatQuery] = useState("");
  const groups = useMemo(() => groupCategories(categories), [categories]);
  const visible = useMemo(() => filterGroups(groups, catQuery), [groups, catQuery]);
  const sections = useMemo(() => byInitial(visible), [visible]);
  const selected = groups.filter((g) => isGroupSelected(g, state.cats));
  const toggle = (g: CategoryGroup) => onChange({ ...state, cats: toggleGroup(g, state.cats) });
  const anyActive = activeFilterCount(state, groups) > 0;
  // Slova se lijepe tačno ispod zaglavlja kategorija; njegova visina zavisi od fonta i
  // prelamanja, pa se mjeri umjesto da se pogađa.
  const [stickTop, setStickTop] = useState(0);
  const measureHeader = useCallback((el: HTMLDivElement | null) => {
    if (!el) return;
    const ro = new ResizeObserver(() => setStickTop(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full gap-0 p-0 sm:max-w-md">
        <SheetHeader className="border-b px-5 py-4">
          <SheetTitle className="text-base">Filteri</SheetTitle>
          <SheetDescription className="sr-only">Suzi katalog po zalihi, shopu i kategoriji.</SheetDescription>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <section className="space-y-2.5 border-b px-5 py-4">
            <h3 className="text-xs font-medium">Zaliha</h3>
            <Segment
              label="Zaliha"
              value={state.inStock ? "in" : "all"}
              onChange={(v) => onChange({ ...state, inStock: v === "in" })}
              options={[{ value: "in", label: "Samo na zalihi" }, { value: "all", label: "Sve, i rasprodano" }]}
            />
          </section>

          {sources.length > 1 && (
            <section className="space-y-2.5 border-b px-5 py-4">
              <h3 className="text-xs font-medium">Shop</h3>
              <Segment
                label="Shop"
                value={state.store}
                onChange={(v) => onChange({ ...state, store: v })}
                options={[{ value: ALL_STORES, label: "Svi shopovi" }, ...sources.map((s) => ({ value: s.id, label: s.name }))]}
              />
            </section>
          )}

          <section>
            {/* Naslov i pretraga se lijepe za vrh: lista zna imati dvjesto kategorija, a
                traženje mora ostati na dohvat i kad je skrolano do Ž. */}
            <div ref={measureHeader} className="sticky top-0 z-10 space-y-2.5 border-b bg-background px-5 pb-3 pt-4">
              <div className="flex items-baseline justify-between">
                <h3 className="text-xs font-medium">Kategorija</h3>
                {selected.length > 0 && (
                  <button
                    type="button"
                    onClick={() => onChange({ ...state, cats: [] })}
                    className={cn("text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline", focusRing)}
                  >
                    Očisti ({selected.length})
                  </button>
                )}
              </div>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={catQuery}
                  onChange={(e) => setCatQuery(e.target.value)}
                  placeholder="Nađi kategoriju"
                  aria-label="Nađi kategoriju"
                  className="h-9 pl-9 pr-9"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                />
                {catQuery && (
                  <button
                    type="button"
                    onClick={() => setCatQuery("")}
                    aria-label="Očisti pretragu kategorija"
                    className="absolute right-0 top-0 flex h-full w-9 items-center justify-center text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Odabrane kategorije stoje na vrhu dok se ne traži — da se vidi šta je
                uključeno bez listanja kroz cijelu abecedu. */}
            {!catQuery && selected.length > 0 && (
              <div className="border-b-4 border-double border-foreground/15">
                <ul className="py-1">
                  {selected.map((g) => <CategoryRow key={g.key} group={g} checked onToggle={() => toggle(g)} />)}
                </ul>
              </div>
            )}

            {sections.length === 0 ? (
              <p className="px-5 py-8 text-sm text-muted-foreground">
                {catQuery ? `Nijedna kategorija ne sadrži „${catQuery.trim()}".` : "Shop nema kategorija."}
              </p>
            ) : (
              sections.map(([letter, gs]) => (
                <div key={letter}>
                  <div
                    style={{ top: stickTop }}
                    className="sticky z-[5] border-b border-foreground/10 bg-muted px-5 py-1 text-xs font-medium text-muted-foreground"
                  >
                    {letter}
                  </div>
                  <ul className="py-1">
                    {gs.map((g) => (
                      <CategoryRow key={g.key} group={g} checked={isGroupSelected(g, state.cats)} onToggle={() => toggle(g)} />
                    ))}
                  </ul>
                </div>
              ))
            )}
          </section>
        </div>

        <div className="flex items-center gap-2 border-t bg-background px-5 py-3">
          <button
            type="button"
            onClick={onReset}
            disabled={!anyActive}
            className={cn(
              "h-11 px-3 text-sm text-muted-foreground transition-colors hover:text-foreground disabled:pointer-events-none disabled:opacity-40",
              focusRing,
            )}
          >
            Poništi sve
          </button>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className={cn(
              "ml-auto inline-flex h-11 flex-1 items-center justify-center gap-2 bg-foreground px-4 text-sm font-medium text-background transition-opacity hover:opacity-90 sm:flex-none sm:min-w-56",
              focusRing,
            )}
          >
            {counting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {total === 0 && !counting ? "Nema artikala" : `Prikaži ${total.toLocaleString("bs-BA")} artikala`}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
